import { useProfile } from '@/contexts/CurrentUserContext/hooks';
import type { ProfileFragment } from '@/fragments/profile.generated';
import { OrganizationRole, OrganizationType } from '@/types/gql.generated';

export type Organization = ProfileFragment['organizations'][number];

// `role` is the viewer's own role in the org, not a property of the org. Distinct
// from the experience-level `Role` enum behind collaborator permissions and the
// owner-only checks the billing gates' call sites apply.
export const isOrganizationOwner = (org: Organization | null) =>
  org?.role === OrganizationRole.Owner;

// Shared orgs are ordered for display: ones the user owns come before ones they
// only belong to, and within each group family orgs come first. Ties keep the
// profile query's order (createdAt asc). The personal org is surfaced
// separately and always leads the list in the UI.
const ownerRank = (org: Organization) => (isOrganizationOwner(org) ? 0 : 1);
const familyRank = (org: Organization) =>
  org.type === OrganizationType.Family ? 0 : 1;

// Organizations ride on the profile query, so they're available everywhere
// the user is (no separate fetch); mutations that change org data invalidate
// the profile query key.
export const useOrganizations = () => {
  const { currentUser, isLoading, isError, refetch } = useProfile();

  const organizations = currentUser?.organizations ?? [];

  const sharedOrganizations = organizations
    .filter((org) => org.type !== OrganizationType.Personal)
    .sort(
      (a, b) => ownerRank(a) - ownerRank(b) || familyRank(a) - familyRank(b)
    );

  return {
    organizations,
    // The user's PERSONAL org-of-1 (their default home context) vs the orgs
    // that surface as organizations in the product (settings, share targets,
    // switcher entries).
    personalOrganization:
      organizations.find((org) => org.type === OrganizationType.Personal) ??
      null,
    sharedOrganizations,
    isLoading,
    isError,
    refetch,
  };
};
